import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Download as DownloadIcon } from 'react-feather';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import esLocale from 'date-fns/locale/es';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CustomTextField from '../../../components/Inputs/TextField/CustomTextField';
import CustomAutocomplete from '../../../components/Inputs/Autocomplete/CustomAutocomplete';
import { getOrdersXlsx } from '../../../requests/api/order';
import storeRequest from '../../../requests/api/store';
import handleApiResponse from '../../../utils/handleApiResponse';

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

function autoColWidths(rows) {
  if (!rows.length) return [];
  const keys = Object.keys(rows[0]);
  return keys.map((key) => ({
    wch: rows.reduce(
      (max, row) => Math.max(
        max,
        String(row[key] !== null && row[key] !== undefined ? row[key] : '').length,
      ),
      key.length,
    ),
  }));
}

function toDateStr(date) {
  return date.toISOString().substring(0, 10);
}

export default function DatesReportPanel() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.account);
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [endDate, setEndDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
  );
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await storeRequest.getStoresPerPage(0, 500, '', user.clientId);
        const fetchedStores = response.allStores || [];
        setStores(fetchedStores);
        if (fetchedStores.length) setSelectedStore(fetchedStores[0]);
      } catch (err) {
        handleApiResponse(enqueueSnackbar, err, false);
      }
    };
    fetchStores();
  }, [user.clientId]);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const response = await getOrdersXlsx(
        user.clientId,
        toDateStr(startDate),
        toDateStr(endDate),
        selectedStore ? selectedStore.id : '',
      );

      const d = response.data || {};
      const wb = XLSX.utils.book_new();

      const orders = (d.orders || []).map((o) => ({
        Local: o.local,
        'N° Pedido': o.numero_pedido,
        Total: o.total,
        Estado: o.status,
        Fecha: o.fecha || '',
        Subtotal: o.subtotal,
        Propina: o.propina != null ? o.propina : '',
        Tipo: o.type,
      }));
      const wsOrders = XLSX.utils.json_to_sheet(orders);
      if (orders.length) wsOrders['!cols'] = autoColWidths(orders);
      XLSX.utils.book_append_sheet(wb, wsOrders, 'Pedidos');

      const resume = (d.resume || []).map((r) => ({
        Local: r.local,
        Total: r.total,
        Subtotal: r.subtotal,
        Propina: r.propina != null ? r.propina : '',
      }));
      const wsResume = XLSX.utils.json_to_sheet(resume);
      if (resume.length) wsResume['!cols'] = autoColWidths(resume);
      XLSX.utils.book_append_sheet(wb, wsResume, 'Resumen');

      const quantities = (d.quantities || []).map((q) => ({
        Local: q.local,
        Producto: q.producto,
        Cantidad: q.cantidad,
        'Cantidad S/': q.cantidad_s != null ? q.cantidad_s : '',
      }));
      const wsQuantities = XLSX.utils.json_to_sheet(quantities);
      if (quantities.length) wsQuantities['!cols'] = autoColWidths(quantities);
      XLSX.utils.book_append_sheet(wb, wsQuantities, 'Cantidades');

      XLSX.writeFile(wb, `pedidos_${toDateStr(startDate)}_${toDateStr(endDate)}.xlsx`);
    } catch (err) {
      handleApiResponse(enqueueSnackbar, err, false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h5" className={classes.title}>
          Descargar reporte por fechas
        </Typography>
        <Box className={classes.row}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
            <DatePicker
              views={['day', 'month', 'year']}
              label="Fecha de inicio"
              value={startDate}
              onChange={(value) => { if (value) setStartDate(value); }}
              renderInput={(params) => (
                <CustomTextField {...params} variant="outlined" />
              )}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
            <DatePicker
              views={['day', 'month', 'year']}
              label="Fecha límite"
              value={endDate}
              onChange={(value) => { if (value) setEndDate(value); }}
              renderInput={(params) => (
                <CustomTextField {...params} variant="outlined" />
              )}
            />
          </LocalizationProvider>
          <CustomAutocomplete
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Sin locales para mostrar"
            options={stores}
            value={selectedStore}
            getOptionLabel={(option) => option.name || ''}
            onChange={(e, value) => setSelectedStore(value)}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Local"
                variant="outlined"
                style={{ minWidth: 200 }}
              />
            )}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            disabled={loading}
            startIcon={<DownloadIcon size={16} />}
          >
            {loading ? 'Descargando...' : 'Descargar'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
